import React, { useState, useRef, useEffect } from 'react';
import { MagnifyingGlass, X, Star, CaretDown, Funnel } from '@phosphor-icons/react';

const SUBCATEGORY_OPTIONS = ['hotel', 'hostel', 'guest_house', 'motel', 'apartment'];

const AccommodationFilters = ({
  cities = [],
  selectedCities = [],
  onCitiesChange,
  priceMin,
  priceMax,
  onPriceChange,
  subcategory,
  onSubcategoryChange,
  starsMin,
  onStarsMinChange,
  searchText = '',
  onSearchChange,
  onApply,
  onClear,
  loading = false,
}) => {
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setCityDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggleCity = (city) => {
    if (selectedCities.includes(city)) {
      if (selectedCities.length > 1) {
        onCitiesChange(selectedCities.filter((c) => c !== city));
      }
    } else {
      onCitiesChange([...selectedCities, city]);
    }
  };

  return (
    <div className="ls-filter-bar ls-elevated p-2 sm:p-3 mb-3 sm:mb-4 rounded-lg">
      {/* Row 1: Search + City + Apply */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2 mb-2">
        {/* Search */}
        <div className="relative flex-1 min-w-0 sm:min-w-[180px]">
          <MagnifyingGlass
            size={14}
            weight="bold"
            className="absolute left-2.5 top-1/2 -translate-y-1/2"
            style={{ color: 'var(--ls-muted)' }}
          />
          <input
            type="text"
            className="ls-input w-full !pl-8 !py-2 text-sm"
            placeholder="Search venues..."
            value={searchText}
            onChange={(e) => onSearchChange(e.target.value)}
            data-testid="ls-filter-search"
          />
        </div>

        {/* City multi-select */}
        <div className="relative" ref={dropdownRef}>
          <button
            className="ls-btn-ghost !py-2 text-xs flex items-center gap-1.5"
            onClick={() => setCityDropdownOpen(!cityDropdownOpen)}
            data-testid="ls-filter-cities"
          >
            <Funnel size={12} weight="bold" />
            Cities
            {selectedCities.length > 0 && (
              <span
                className="text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center"
                style={{ background: 'var(--ls-primary)', color: 'var(--ls-primary-fg)' }}
              >
                {selectedCities.length}
              </span>
            )}
            <CaretDown size={10} weight="bold" />
          </button>
          {cityDropdownOpen && (
            <div
              className="ls-dropdown absolute top-full left-0 mt-1 z-50 ls-surface p-2 min-w-[180px] shadow-lg"
              style={{ border: '1px solid var(--ls-border)', borderRadius: '6px' }}
            >
              {cities.map((city) => (
                <label
                  key={city}
                  className="flex items-center gap-2 px-2 py-1.5 text-xs cursor-pointer rounded hover:bg-[var(--ls-elevated)]"
                >
                  <input
                    type="checkbox"
                    checked={selectedCities.includes(city)}
                    onChange={() => toggleCity(city)}
                    className="accent-[var(--ls-primary)]"
                  />
                  {city}
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Apply */}
        <div className="flex items-center gap-2">
          <div className="relative" ref={dropdownRef}>
            <button
              className="ls-btn-ghost !py-2 text-xs flex items-center gap-1.5 sm:hidden"
              onClick={() => setCityDropdownOpen(!cityDropdownOpen)}
              data-testid="ls-filter-cities-mobile"
            >
              <Funnel size={12} weight="bold" />
              Cities
              {selectedCities.length > 0 && (
                <span
                  className="text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center"
                  style={{ background: 'var(--ls-primary)', color: 'var(--ls-primary-fg)' }}
                >
                  {selectedCities.length}
                </span>
              )}
              <CaretDown size={10} weight="bold" />
            </button>
          </div>
          <button
            className="ls-btn-primary !py-2 text-xs flex-1 sm:flex-none justify-center"
            onClick={onApply}
            disabled={loading || selectedCities.length === 0}
            data-testid="ls-filter-apply"
          >
            {loading ? 'Loading...' : 'Search'}
          </button>
          <button
            className="ls-btn-ghost !py-2 text-xs"
            onClick={onClear}
            data-testid="ls-filter-clear"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Row 2: Subcategory pills + Price + Stars */}
      <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-1">
        {/* Subcategory pills */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            className={`text-[11px] px-2.5 py-1 rounded-full font-semibold transition-colors ${
              !subcategory
                ? 'text-[var(--ls-primary-fg)]'
                : ''
            }`}
            style={{
              background: !subcategory ? 'var(--ls-primary)' : 'var(--ls-elevated)',
              color: !subcategory ? 'var(--ls-primary-fg)' : 'var(--ls-muted)',
              border: '1px solid ' + (!subcategory ? 'var(--ls-primary)' : 'var(--ls-border)'),
            }}
            onClick={() => onSubcategoryChange(null)}
          >
            All
          </button>
          {SUBCATEGORY_OPTIONS.map((sc) => (
            <button
              key={sc}
              className="text-[11px] px-2.5 py-1 rounded-full font-semibold transition-colors"
              style={{
                background: subcategory === sc ? 'var(--ls-primary)' : 'var(--ls-elevated)',
                color: subcategory === sc ? 'var(--ls-primary-fg)' : 'var(--ls-muted)',
                border: '1px solid ' + (subcategory === sc ? 'var(--ls-primary)' : 'var(--ls-border)'),
              }}
              onClick={() => onSubcategoryChange(subcategory === sc ? null : sc)}
            >
              {sc.replace(/_/g, ' ')}
            </button>
          ))}
        </div>

        <span className="text-[10px] flex-shrink-0" style={{ color: 'var(--ls-border)' }}>|</span>

        {/* Price range */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <span className="text-[10px] font-semibold" style={{ color: 'var(--ls-muted)' }}>£</span>
          <input
            type="number"
            className="ls-input !w-16 !py-1 !px-2 text-xs"
            placeholder="Min"
            value={priceMin ?? ''}
            onChange={(e) => onPriceChange('min', e.target.value ? Number(e.target.value) : null)}
            min={0}
          />
          <span className="text-[10px]" style={{ color: 'var(--ls-muted)' }}>—</span>
          <input
            type="number"
            className="ls-input !w-16 !py-1 !px-2 text-xs"
            placeholder="Max"
            value={priceMax ?? ''}
            onChange={(e) => onPriceChange('max', e.target.value ? Number(e.target.value) : null)}
            min={0}
          />
        </div>

        <span className="text-[10px] flex-shrink-0" style={{ color: 'var(--ls-border)' }}>|</span>

        {/* Stars */}
        <div className="flex items-center gap-0.5 flex-shrink-0">
          {[1, 2, 3, 4, 5].map((s) => (
            <button
              key={s}
              onClick={() => onStarsMinChange(starsMin === s ? null : s)}
              className="p-0.5"
              title={`Min ${s} stars`}
            >
              <Star
                size={14}
                weight={starsMin && s <= starsMin ? 'fill' : 'regular'}
                style={{ color: starsMin && s <= starsMin ? 'var(--ls-primary)' : 'var(--ls-muted)' }}
              />
            </button>
          ))}
          {starsMin && (
            <button
              className="text-[10px] ml-1"
              style={{ color: 'var(--ls-muted)' }}
              onClick={() => onStarsMinChange(null)}
            >
              <X size={10} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccommodationFilters;
